//frontend/src/components/admin/ReportGenerator.js
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
    Calendar,
    Download,
    FileSpreadsheet,
    FileText,
    RefreshCw,
    FileDown,
    FileBarChart,
    Calendar as CalendarIcon,
    Users,
    ShoppingBag,
    Truck,
    ArrowLeft
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const ReportGenerator = () => {
    const navigate = useNavigate();
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [format, setFormat] = useState('excel');
    const [loading, setLoading] = useState(false);

    const generateReport = async (endpoint, filename) => {
        try {
            setLoading(true);
            const response = await axios.get(
                `/api/v1/admin/reports/${endpoint}`,
                {
                    responseType: 'blob',
                    withCredentials: true
                }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Report generated successfully');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error generating report');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateCustomReport = async () => {
        if (!startDate || !endDate) {
            toast.error('Please select both start and end dates');
            return;
        }
        if (new Date(startDate) > new Date(endDate)) {
            toast.error('Start date cannot be after end date');
            return;
        }
        try {
            setLoading(true);
            const response = await axios.get(
                `/api/v1/admin/reports/custom?startDate=${startDate}&endDate=${endDate}&format=${format}`,
                {
                    responseType: 'blob',
                    withCredentials: true
                }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `delivery_report_${startDate}_to_${endDate}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Report generated successfully');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error generating report');
        } finally {
            setLoading(false);
        }
    };

    const FormatButton = ({ type, icon: Icon, label }) => (
        <button
            type="button"
            onClick={() => setFormat(type)}
            className={`flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg border transition-all duration-200 text-xs sm:text-sm ${
                format === type
                    ? 'bg-red-50 text-red-600 border-red-600 ring-2 ring-red-200'
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
            }`}
        >
            <Icon className={`w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 ${format === type ? 'text-red-600' : 'text-gray-500'}`} />
            {label}
        </button>
    );

    // Updated component with smaller, more compact buttons for mobile
    const ReportSection = ({ title, icon: Icon, description, buttonText, onClick }) => (
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="flex-shrink-0">
                    <div className="p-2 sm:p-3 bg-red-50 rounded-lg">
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
                    </div>
                </div>
                <div className="flex-1">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">{title}</h3>
                    <p className="mt-1 text-xs sm:text-sm text-gray-500">{description}</p>
                    <button
                        onClick={onClick}
                        disabled={loading}
                        className="mt-3 sm:mt-4 inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-red-600 text-white text-xs sm:text-sm rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 animate-spin" />
                        ) : (
                            <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                        )}
                        {loading ? 'Generating...' : buttonText}
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header with title and back button moved to top right */}
                <div className="flex justify-between items-center mb-4 sm:mb-6">
                    <h1 className="text-lg sm:text-xl font-bold text-red-600 font-['Crimson_Text']">
                        Generate Reports
                    </h1>
                    <button
                        onClick={() => navigate('/admin/dashboard')}
                        className="flex items-center gap-1 sm:gap-2 text-gray-600 hover:text-red-600 transition-colors py-1.5 sm:py-2 px-2.5 sm:px-3 rounded-lg hover:bg-gray-100"
                    >
                        <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-xs sm:text-sm hidden xs:inline">Back to Dashboard</span>
                    </button>
                </div>
                
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="p-4 sm:p-6 border-b border-gray-200">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <FileBarChart className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                            <h2 className="text-base sm:text-xl font-bold text-red-600 font-['Crimson_Text']">
                                Report Options
                            </h2>
                        </div>
                    </div>
                    <div className="p-4 sm:p-6 space-y-6 sm:space-y-8">
                        {/* Date Range Selection - Optimized for mobile */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                    Start Date
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-2 sm:left-3 flex items-center pointer-events-none">
                                        <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="block w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-red-600 focus:border-red-600 transition-colors"
                                    />
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                    End Date
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-2 sm:left-3 flex items-center pointer-events-none">
                                        <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="block w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-red-600 focus:border-red-600 transition-colors"
                                    />
                                </div>
                            </div>
                        </div>
                        
                        {/* Format Selection - More compact for mobile */}
                        <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3">
                                Report Format
                            </label>
                            <div className="flex flex-wrap gap-3 sm:gap-4">
                                <FormatButton
                                    type="excel"
                                    icon={FileSpreadsheet}
                                    label="Excel Report"
                                />
                                <FormatButton
                                    type="pdf"
                                    icon={FileText}
                                    label="PDF Report"
                                />
                            </div>
                        </div>
                        
                        {/* Report Types - Responsive grid for mobile */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                            <ReportSection
                                title="Custom Date Range"
                                icon={Calendar}
                                description="Generate a detailed report for a specific date range including all delivery and order statistics."
                                buttonText="Generate Custom Report"
                                onClick={handleGenerateCustomReport}
                            />
                            <ReportSection
                                title="Monthly Summary"
                                icon={FileDown}
                                description="Generate a comprehensive report for the current month with key performance metrics."
                                buttonText="Generate Monthly Report"
                                onClick={() => generateReport('monthly', `monthly_report.${format === 'excel' ? 'xlsx' : 'pdf'}`)}
                            />
                            <ReportSection
                                title="Users Report"
                                icon={Users}
                                description="Download a complete list of all users with their details and account status."
                                buttonText="Generate Users Report"
                                onClick={() => generateReport('users', `users_report_${Date.now()}.xlsx`)}
                            />
                            <ReportSection
                                title="Products Report"
                                icon={ShoppingBag}
                                description="Export a detailed list of all products including pricing and stock status."
                                buttonText="Generate Products Report"
                                onClick={() => generateReport('products', `products_report_${Date.now()}.xlsx`)}
                            />
                            <ReportSection
                                title="Delivery Staff"
                                icon={Truck}
                                description="Download a comprehensive list of all delivery staff members and their details."
                                buttonText="Generate Staff Report"
                                onClick={() => generateReport('delivery-staff', `delivery_staff_report_${Date.now()}.xlsx`)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportGenerator;